import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Keeps the active tab in the URL query string (e.g. `?tab=executions`) instead
 * of local component state. The page-level refresh button in `Layout` bumps a
 * `key` on the routed `<Outlet>`, which remounts the page and would otherwise
 * reset a `useState` tab back to the first one; storing it in the URL means the
 * tab survives that remount, a real browser reload, and back/forward — the same
 * behaviour the react-admin dashboard gets from path-based tabs.
 *
 * `validTabs` is the full set of known tab slugs; a missing or unrecognised
 * `?tab=` value (typo, stale link) falls back to `defaultTab`.
 *
 * Changing tab pushes a history entry, so the browser back button steps back
 * through the tabs you visited, matching the dashboard's behaviour.
 */
export function useTabParam<T extends string>(
  defaultTab: T,
  validTabs: readonly T[],
  paramName = 'tab',
): [T, (next: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  // Narrow via `includes` rather than a blind cast: an arbitrary `?tab=` value
  // is not necessarily a `T`, so we validate it against the known slugs first.
  const raw = searchParams.get(paramName);
  const tab = raw && (validTabs as readonly string[]).includes(raw) ? (raw as T) : defaultTab;

  const setTab = useCallback(
    (next: T) => {
      setSearchParams((prev) => {
        const updated = new URLSearchParams(prev);
        if (next === defaultTab) {
          // Keep the URL clean on the default tab.
          updated.delete(paramName);
        } else {
          updated.set(paramName, next);
        }
        return updated;
      });
    },
    [setSearchParams, defaultTab, paramName],
  );

  return [tab, setTab];
}
